import { ChangeDetectionStrategy, Component, WritableSignal, computed, inject, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, catchError, finalize, of } from 'rxjs';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { CostReportsProxyService } from '../../../proxies/cost-reports.proxy.service';
import { CostAnalysisStateService } from '../../cost-analysis/services/cost-analysis-state.service';

@Component({
  selector: 'app-reports-page',
  templateUrl: './reports-page.component.html',
  styleUrls: ['./reports-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportsPageComponent {
  private readonly costReportsProxy = inject(CostReportsProxyService);
  private readonly state = inject(CostAnalysisStateService);

  readonly yearOptions = Array.from({ length: new Date().getFullYear() - 2020 + 1 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { label: year.toString(), value: year };
  });
  readonly monthOptions: { label: string; key: string; value: number }[] = [
    { label: 'January', key: 'Month_Jan', value: 1 },
    { label: 'February', key: 'Month_Feb', value: 2 },
    { label: 'March', key: 'Month_Mar', value: 3 },
    { label: 'April', key: 'Month_Apr', value: 4 },
    { label: 'May', key: 'Month_May', value: 5 },
    { label: 'June', key: 'Month_Jun', value: 6 },
    { label: 'July', key: 'Month_Jul', value: 7 },
    { label: 'August', key: 'Month_Aug', value: 8 },
    { label: 'September', key: 'Month_Sep', value: 9 },
    { label: 'October', key: 'Month_Oct', value: 10 },
    { label: 'November', key: 'Month_Nov', value: 11 },
    { label: 'December', key: 'Month_Dec', value: 12 },
  ];
  readonly processOptions: { label: string; value: string }[] = [
    { label: 'Knitting', value: 'Knitting' },
    { label: 'Greige', value: 'Greige' },
    { label: 'Batching', value: 'Batching' },
    { label: 'Dyeing', value: 'Dyeing' },
    { label: 'Finishing', value: 'Finishing' },
    { label: 'Rolling', value: 'Rolling' },
  ];
  readonly reconciliationOptions: { label: string; value: string }[] = [
    { label: 'Raw Material', value: 'raw-material' },
    { label: 'Labor', value: 'labor' },
    { label: 'Depreciation', value: 'depreciation' },
    { label: 'Consumables', value: 'consumables' },
    { label: 'Chemicals', value: 'chemicals' },
    { label: 'Energy', value: 'energy' },
    { label: 'Water', value: 'water' },
    { label: 'Inoptsa', value: 'inoptsa' },
    { label: 'Steam', value: 'steam' },
    { label: 'Bunker', value: 'bunker' },
    { label: 'Rent', value: 'rent' },
    { label: 'Waste Knitting', value: 'waste-knitting' },
    { label: 'Waste Area', value: 'waste-area' },
    { label: 'Scrap', value: 'scrap' },
  ];

  readonly dailyProductionForm = new FormGroup({
    fromDate: new FormControl<Date | null>(this.startOfDay(), [Validators.required]),
    toDate: new FormControl<Date | null>(this.endOfDay(), [Validators.required]),
    processes: new FormControl<string[]>(
      this.processOptions.map((option) => option.value),
      { nonNullable: true, validators: [Validators.required] },
    ),
  });

  readonly reconciliationForm = new FormGroup({
    year: new FormControl<number | null>(this.state.selectedYear(), [Validators.required]),
    month: new FormControl<number | null>(this.state.selectedMonth(), [Validators.required]),
    costKey: new FormControl<string | null>(null, [Validators.required]),
  });

  readonly loadingDailyProduction = signal<boolean>(false);
  readonly loadingReconciliation = signal<boolean>(false);

  readonly isAnyReportLoading = computed(() => this.loadingDailyProduction() || this.loadingReconciliation());

  generateDailyProductionReport(): void {
    if (!this.dailyProductionForm.valid) {
      GlobalMessageUtil.warn('Validation Error', 'Please fill in all required fields');
      return;
    }
    const from = new Date(this.dailyProductionForm.value.fromDate!);
    from.setSeconds(0, 0);
    const to = new Date(this.dailyProductionForm.value.toDate!);
    to.setSeconds(59, 0);
    const offsetMinutes = -new Date().getTimezoneOffset();
    this.download(
      this.costReportsProxy.downloadDailyProductionExcel(from, to, offsetMinutes, this.dailyProductionForm.getRawValue().processes),
      `daily-production_${this.isoDate(from)}_${this.isoDate(to)}.xlsx`,
      'Daily Production',
      this.loadingDailyProduction,
    );
  }

  generateReconciliationReport(): void {
    if (!this.reconciliationForm.valid) {
      GlobalMessageUtil.warn('Validation Error', 'Please fill in all required fields');
      return;
    }
    const { year, month, costKey } = this.reconciliationForm.value;
    this.download(
      this.costReportsProxy.downloadReconciliationExcel(costKey!, year!, month!),
      `${costKey}-reconciliation_${year}-${this.pad2(month!)}.xlsx`,
      'Reconciliation',
      this.loadingReconciliation,
    );
  }

  private download(request: Observable<Blob>, filename: string, reportName: string, loading: WritableSignal<boolean>): void {
    loading.set(true);
    request
      .pipe(
        catchError(() => of(null)),
        finalize(() => loading.set(false)),
      )
      .subscribe((blob) => {
        if (!blob) {
          GlobalMessageUtil.error('Error', `Failed to generate ${reportName} report`);
          return;
        }
        this.triggerDownload(blob, filename);
        GlobalMessageUtil.success('Success', `${reportName} report generated successfully`);
      });
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  private startOfDay(): Date {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private endOfDay(): Date {
    const date = new Date();
    date.setHours(23, 59, 0, 0);
    return date;
  }

  private isoDate(date: Date): string {
    return `${date.getFullYear()}-${this.pad2(date.getMonth() + 1)}-${this.pad2(date.getDate())}`;
  }

  private pad2(n: number): string {
    return String(n).padStart(2, '0');
  }
}
